import { useState, useEffect, useRef } from 'react';
import { API, formatWeight, formatDateTime } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { useNavigate } from 'react-router-dom';
import {
  Box, ShoppingCart, CheckSquare, Users, TrendingUp,
  FileText, AlertTriangle, Package, Key, Clock, Monitor, XCircle, ClipboardCheck, ChevronDown, ChevronUp, FileSpreadsheet
} from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { toast } from 'sonner';
import { BackupCard } from '@/components/BackupCard';

const MAX_RETRIES = 3;
const RETRY_DELAYS = [5000, 10000, 30000]; // 5s, 10s, 30s

export default function DashboardPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [salesTrend, setSalesTrend] = useState([]);
  const [stockSummary, setStockSummary] = useState([]);
  const [otpRequests, setOtpRequests] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [showOtps, setShowOtps] = useState(false);
  const [showSessions, setShowSessions] = useState(false);
  const [backupStatus, setBackupStatus] = useState(null);
  const [dashLoading, setDashLoading] = useState(true);
  const [dashError, setDashError] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [reconLogs, setReconLogs] = useState([]);
  const [expandedRecon, setExpandedRecon] = useState({});
  const retryRef = useRef(null);
  const retryCountRef = useRef(0);

  const isAdmin = ['admin', 'analyst'].includes(user?.role);
  const canSeeWeights = ['admin', 'store_manager'].includes(user?.role);
  const canSeeOtp = ['admin', 'store_manager'].includes(user?.role);

  const fetchSessions = async () => {
    if (!isAdmin) return;
    try { const res = await API.get('/sessions'); setSessions(res.data); } catch(e) {}
  };

  const terminateAllSessions = async () => {
    if (!window.confirm('End ALL active sessions? All users will be logged out.')) return;
    try {
      const res = await API.delete('/sessions');
      toast.success(`${res.data.terminated} sessions terminated`);
      fetchSessions();
    } catch(err) { toast.error('Failed to end sessions'); }
  };

  const terminateSession = async (sessionId) => {
    try {
      await API.delete(`/sessions/${sessionId}`);
      toast.success('Session terminated');
      fetchSessions();
    } catch(err) { toast.error('Failed to terminate session'); }
  };

  useEffect(() => {
    const fetchData = async () => {
      setDashLoading(true);
      setDashError(false);
      try {
        const results = await Promise.allSettled([
          API.get('/dashboard/stats'),
          API.get('/analytics/sales-trend?days=7'),
          API.get('/analytics/stock-summary'),
        ]);
        if (results[0].status === 'fulfilled') setStats(results[0].value.data);
        if (results[1].status === 'fulfilled') setSalesTrend(results[1].value.data);
        if (results[2].status === 'fulfilled') setStockSummary(results[2].value.data);
        const allFailed = !results.some(r => r.status === 'fulfilled');
        if (allFailed) {
          setDashError(true);
          // Schedule retry with capped backoff
          if (retryCountRef.current < MAX_RETRIES) {
            const delay = RETRY_DELAYS[retryCountRef.current] || RETRY_DELAYS[RETRY_DELAYS.length - 1];
            retryCountRef.current += 1;
            retryRef.current = setTimeout(fetchData, delay);
          }
        } else {
          retryCountRef.current = 0; // reset on any success
          setLastUpdated(new Date());
        }
      } catch(e) {
        setDashError(true);
        if (retryCountRef.current < MAX_RETRIES) {
          const delay = RETRY_DELAYS[retryCountRef.current] || RETRY_DELAYS[RETRY_DELAYS.length - 1];
          retryCountRef.current += 1;
          retryRef.current = setTimeout(fetchData, delay);
        }
      } finally {
        setDashLoading(false);
      }
    };
    fetchData();

    let otpInterval = null;
    if (canSeeOtp) {
      const fetchOtps = async () => {
        try { const res = await API.get('/auth/otp-requests'); setOtpRequests(res.data); } catch(e) {}
      };
      fetchOtps();
      otpInterval = setInterval(fetchOtps, 5000);
    }

    return () => {
      if (retryRef.current) clearTimeout(retryRef.current);
      if (otpInterval) clearInterval(otpInterval);
    };
  }, [user?.role]);

  const fetchBackupStatus = async () => {
    if (!isAdmin) return;
    try { const res = await API.get('/backup/status'); setBackupStatus(res.data); } catch(e) {}
  };

  const fetchReconLogs = async () => {
    if (!isAdmin) return;
    try { const res = await API.get('/boxes/reconciliation/logs'); setReconLogs(res.data); } catch(e) {}
  };

  useEffect(() => {
    fetchSessions();
    fetchBackupStatus();
    fetchReconLogs();
    if (isAdmin) {
      const sessionInterval = setInterval(fetchSessions, 15000);
      return () => clearInterval(sessionInterval);
    }
  }, [isAdmin]);

  const quickActions = [
    { label: 'New Sale', icon: ShoppingCart, path: '/daily-ops', color: 'bg-emerald-950/40 text-emerald-400 border-emerald-800', roles: ['admin', 'store_manager', 'stock_manager'] },
    { label: 'Stock Taking', icon: Package, path: '/stock-taking', color: 'bg-blue-950/40 text-blue-400 border-blue-800', roles: ['admin', 'store_manager', 'stock_manager'] },
    { label: 'Boxes', icon: Box, path: '/boxes', color: 'bg-amber-950/40 text-amber-400 border-amber-800', roles: ['admin', 'store_manager', 'stock_manager'] },
    { label: 'ST Results', icon: ClipboardCheck, path: '/stock-taking-results', color: 'bg-purple-950/40 text-purple-400 border-purple-800', roles: ['admin', 'store_manager'] },
    { label: 'Approvals', icon: CheckSquare, path: '/approvals', color: 'bg-rose-950/40 text-rose-400 border-rose-800', roles: ['admin', 'store_manager'] },
  ].filter(a => a.roles.includes(user?.role) || (user?.role === 'analyst' && a.roles.includes('admin')));

  const roleColors = {
    admin: 'bg-purple-950/40 text-purple-400',
    store_manager: 'bg-blue-950/40 text-blue-400',
    stock_manager: 'bg-emerald-950/40 text-emerald-400',
    billing_executive: 'bg-amber-950/40 text-amber-400',
  };

  return (
    <div data-testid="dashboard-page">
      <div className="page-header">
        <h1>Dashboard</h1>
        <p>Welcome back, {user?.name}.</p>
      </div>

      {/* Compact OTP & Session Buttons */}
      <div className="flex flex-wrap gap-2 mb-4">
        {canSeeOtp && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowOtps(true)}
              className="relative"
              style={{ background: otpRequests.length > 0 ? 'hsl(43, 50%, 14%)' : undefined, borderColor: otpRequests.length > 0 ? 'hsl(43, 50%, 25%)' : undefined, color: otpRequests.length > 0 ? 'hsl(43, 80%, 65%)' : undefined }}
              data-testid="dashboard-otp-btn"
            >
              <Key size={14} className="mr-1.5" />
              Pending OTPs
              {otpRequests.length > 0 && (
                <span className="ml-1.5 px-1.5 py-0.5 rounded-full text-[10px] font-bold bg-primary text-primary-foreground">{otpRequests.length}</span>
              )}
            </Button>
          )}
          {isAdmin && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowSessions(true)}
              data-testid="dashboard-sessions-btn"
            >
              <Monitor size={14} className="mr-1.5" />
              Active Sessions
              {sessions.length > 0 && (
                <span className="ml-1.5 px-1.5 py-0.5 rounded-full text-[10px] font-bold bg-blue-500 text-white">{sessions.length}</span>
              )}
            </Button>
          )}
        </div>

      {/* KPI Cards */}
      <>
      {dashError && !stats && (
        <div className="mb-4 p-3 rounded-lg bg-destructive/10 border border-destructive/20 text-sm text-destructive flex items-center gap-2" data-testid="dashboard-error">
          <AlertTriangle size={16} />
          {retryCountRef.current < MAX_RETRIES
            ? `Failed to load dashboard data. Retrying in ${RETRY_DELAYS[retryCountRef.current - 1] / 1000}s... (attempt ${retryCountRef.current}/${MAX_RETRIES})`
            : 'Failed to load dashboard data after multiple attempts. Please refresh the page.'}
        </div>
      )}
      {lastUpdated && (
        <p className="text-[10px] text-muted-foreground mb-2" data-testid="dashboard-last-updated">
          Last updated: {lastUpdated.toLocaleTimeString('en-IN', { timeZone: 'Asia/Kolkata', hour: '2-digit', minute: '2-digit', second: '2-digit' })} IST
        </p>
      )}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4 mb-4 md:mb-6">
        <Card className="card-hover">
          <CardContent className="pt-4 md:pt-6 px-3 md:px-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs md:text-sm text-muted-foreground">Active Boxes</p>
                {dashLoading && !stats ? (
                  <div className="h-8 w-16 rounded bg-muted/50 animate-pulse mt-1" />
                ) : (
                  <p className="kpi-number text-foreground" data-testid="kpi-active-boxes">{stats?.active_boxes ?? '—'}</p>
                )}
              </div>
              <div className="w-10 h-10 md:w-12 md:h-12 rounded-xl bg-amber-950/40 flex items-center justify-center">
                <Box className="text-amber-500" size={20} />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="card-hover">
          <CardContent className="pt-4 md:pt-6 px-3 md:px-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs md:text-sm text-muted-foreground">Pending</p>
                {dashLoading && !stats ? (
                  <div className="h-8 w-16 rounded bg-muted/50 animate-pulse mt-1" />
                ) : (
                  <p className="kpi-number text-foreground" data-testid="kpi-pending-approvals">{stats?.pending_approvals ?? '—'}</p>
                )}
              </div>
              <div className="w-10 h-10 md:w-12 md:h-12 rounded-xl bg-rose-950/40 flex items-center justify-center">
                <CheckSquare className="text-rose-400" size={20} />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="card-hover">
          <CardContent className="pt-4 md:pt-6 px-3 md:px-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs md:text-sm text-muted-foreground">Today Sales</p>
                {dashLoading && !stats ? (
                  <div className="h-8 w-16 rounded bg-muted/50 animate-pulse mt-1" />
                ) : (
                  <>
                    <p className="kpi-number text-foreground" data-testid="kpi-today-sales">{stats?.today_sales_g != null ? formatWeight(stats.today_sales_g) : '—'}</p>
                    <p className="text-[10px] text-muted-foreground mt-0.5">net of returns</p>
                  </>
                )}
              </div>
              <div className="w-10 h-10 md:w-12 md:h-12 rounded-xl bg-emerald-950/40 flex items-center justify-center">
                <TrendingUp className="text-emerald-400" size={20} />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="card-hover">
          <CardContent className="pt-4 md:pt-6 px-3 md:px-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs md:text-sm text-muted-foreground">Customers</p>
                {dashLoading && !stats ? (
                  <div className="h-8 w-16 rounded bg-muted/50 animate-pulse mt-1" />
                ) : (
                  <p className="kpi-number text-foreground" data-testid="kpi-total-customers">{stats?.total_customers ?? '—'}</p>
                )}
              </div>
              <div className="w-10 h-10 md:w-12 md:h-12 rounded-xl bg-blue-950/40 flex items-center justify-center">
                <Users className="text-blue-400" size={20} />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 md:gap-3 mb-4 md:mb-6">
        {quickActions.map(action => (
          <Button key={action.label} variant="outline"
            className={`h-auto py-3 md:py-4 flex flex-col gap-1 md:gap-2 ${action.color} border text-xs md:text-sm`}
            onClick={() => navigate(action.path)}
            data-testid={`quick-action-${action.label.toLowerCase().replace(' ', '-')}`}
          >
            <action.icon size={18} /><span className="font-medium">{action.label}</span>
          </Button>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6">
        {/* Stock by Purity */}
        {canSeeWeights && (
          <Card>
            <CardHeader className="pb-2 md:pb-4"><CardTitle className="text-base md:text-lg">Stock by Purity</CardTitle></CardHeader>
            <CardContent>
              <div className="space-y-3">
                {stockSummary.map(s => (
                  <div key={s.purity} className="p-3 rounded-lg bg-muted/50">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <span className={`status-badge purity-${s.purity}`}>{s.purity}</span>
                        <span className="text-xs text-muted-foreground">{s.box_count} boxes</span>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3 text-xs">
                      <div>
                        <span className="text-muted-foreground">Gross Weight</span>
                        <p className="weight-value font-semibold text-sm">{formatWeight(s.total_gross_g)}</p>
                      </div>
                      <div>
                        <span className="text-muted-foreground">Net Gold Weight</span>
                        <p className="weight-value font-semibold text-sm" style={{ color: 'hsl(43, 96%, 56%)' }}>{formatWeight(s.total_gold_g)}</p>
                      </div>
                    </div>
                  </div>
                ))}
                {stockSummary.length === 0 && <p className="text-sm text-muted-foreground text-center py-4">No stock data yet.</p>}
              </div>
            </CardContent>
          </Card>
        )}
        {/* Sales Trend */}
        <Card>
          <CardHeader className="pb-2 md:pb-4"><CardTitle className="text-base md:text-lg">Sales Trend (7 Days)</CardTitle></CardHeader>
          <CardContent>
            {salesTrend.length > 0 ? (
              <ResponsiveContainer width="100%" height={180}>
                <BarChart data={salesTrend}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(36 18% 86%)" />
                  <XAxis dataKey="date" tick={{ fontSize: 10 }} tickFormatter={(d) => new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })} />
                  <YAxis tick={{ fontSize: 10 }} width={30} />
                  <Tooltip />
                  <Bar dataKey="count" fill="hsl(40, 89%, 45%)" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : <p className="text-sm text-muted-foreground text-center py-8">No sales data yet</p>}
          </CardContent>
        </Card>
      </div>

      {/* Reconciliation Log */}
      {isAdmin && reconLogs.length > 0 && (
        <Card className="mt-4 md:mt-6">
          <CardHeader className="pb-2 md:pb-4">
            <CardTitle className="text-base md:text-lg flex items-center gap-2"><FileSpreadsheet size={16} /> Reconciliation Log</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {reconLogs.map(log => (
                <div key={log.id} className="border rounded-lg overflow-hidden" data-testid={`recon-log-${log.id}`}>
                  <button className="w-full flex items-center justify-between p-3 hover:bg-muted/30 transition-colors text-left"
                    onClick={() => setExpandedRecon(prev => ({ ...prev, [log.id]: !prev[log.id] }))}>
                    <div className="flex items-center gap-3">
                      <span className="text-sm font-semibold">{log.date ? log.date.split('-').reverse().join('-') : '—'}</span>
                      <Badge variant="outline" className="text-[10px]">{log.box_count} boxes</Badge>
                      <span className="text-xs text-muted-foreground">by {log.applied_by}</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className={`text-xs font-mono font-bold ${log.totals?.delta_gold > 0 ? 'text-emerald-400' : log.totals?.delta_gold < 0 ? 'text-rose-400' : 'text-muted-foreground'}`}>
                        Gold: {log.totals?.delta_gold > 0 ? '+' : ''}{log.totals?.delta_gold?.toFixed(3) || '0.000'}g
                      </span>
                      {expandedRecon[log.id] ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                    </div>
                  </button>
                  {expandedRecon[log.id] && log.deltas && (
                    <div className="border-t px-3 pb-3 pt-2">
                      <div className="overflow-x-auto">
                        <table className="w-full text-[10px]">
                          <thead>
                            <tr className="border-b">
                              <th className="text-left py-1 pr-2">Box</th>
                              <th className="text-right py-1 px-1">Gross Delta</th>
                              <th className="text-right py-1 px-1">Box Wt Delta</th>
                              <th className="text-right py-1 px-1">Poly Delta</th>
                              <th className="text-right py-1 px-1">Gold Delta</th>
                            </tr>
                          </thead>
                          <tbody>
                            {log.deltas.map(d => (
                              <tr key={d.box_id} className="border-b border-border/30">
                                <td className="py-1 pr-2 font-mono font-semibold">{d.box_id}</td>
                                <td className={`py-1 px-1 text-right font-mono ${d.delta_gross > 0 ? 'text-emerald-400' : d.delta_gross < 0 ? 'text-rose-400' : ''}`}>{d.delta_gross > 0 ? '+' : ''}{d.delta_gross?.toFixed(3)}</td>
                                <td className={`py-1 px-1 text-right font-mono ${d.delta_box_wt > 0 ? 'text-emerald-400' : d.delta_box_wt < 0 ? 'text-rose-400' : ''}`}>{d.delta_box_wt > 0 ? '+' : ''}{d.delta_box_wt?.toFixed(3)}</td>
                                <td className={`py-1 px-1 text-right font-mono ${d.delta_poly > 0 ? 'text-emerald-400' : d.delta_poly < 0 ? 'text-rose-400' : ''}`}>{d.delta_poly > 0 ? '+' : ''}{d.delta_poly?.toFixed(3)}</td>
                                <td className={`py-1 px-1 text-right font-mono font-bold ${d.delta_gold > 0 ? 'text-emerald-400' : d.delta_gold < 0 ? 'text-rose-400' : ''}`}>{d.delta_gold > 0 ? '+' : ''}{d.delta_gold?.toFixed(3)}</td>
                              </tr>
                            ))}
                            <tr className="border-t-2 font-bold">
                              <td className="py-1 pr-2">TOTALS</td>
                              <td className={`py-1 px-1 text-right font-mono ${log.totals.delta_gross > 0 ? 'text-emerald-400' : log.totals.delta_gross < 0 ? 'text-rose-400' : ''}`}>{log.totals.delta_gross > 0 ? '+' : ''}{log.totals.delta_gross?.toFixed(3)}</td>
                              <td className={`py-1 px-1 text-right font-mono ${log.totals.delta_box_wt > 0 ? 'text-emerald-400' : log.totals.delta_box_wt < 0 ? 'text-rose-400' : ''}`}>{log.totals.delta_box_wt > 0 ? '+' : ''}{log.totals.delta_box_wt?.toFixed(3)}</td>
                              <td className={`py-1 px-1 text-right font-mono ${log.totals.delta_poly > 0 ? 'text-emerald-400' : log.totals.delta_poly < 0 ? 'text-rose-400' : ''}`}>{log.totals.delta_poly > 0 ? '+' : ''}{log.totals.delta_poly?.toFixed(3)}</td>
                              <td className={`py-1 px-1 text-right font-mono font-bold ${log.totals.delta_gold > 0 ? 'text-emerald-400' : log.totals.delta_gold < 0 ? 'text-rose-400' : ''}`}>{log.totals.delta_gold > 0 ? '+' : ''}{log.totals.delta_gold?.toFixed(3)}</td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                      <p className="text-[10px] text-muted-foreground mt-2">{formatDateTime(log.created_at)}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Admin Backup Card */}
      {isAdmin && (
        <div className="mt-4 md:mt-6 max-w-md">
          <BackupCard backupStatus={backupStatus} onRefresh={fetchBackupStatus} />
        </div>
      )}
      </>

      {/* OTP Dialog */}
      <Dialog open={showOtps} onOpenChange={setShowOtps}>
        <DialogContent className="max-w-md max-h-[80vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Key size={18} /> Pending Login OTPs</DialogTitle></DialogHeader>
          {otpRequests.length > 0 ? (
            <div className="space-y-3">
              {otpRequests.map(otp => (
                <div key={otp.id} className="p-3 rounded-lg border" style={{ background: 'hsl(30, 8%, 11%)', borderColor: 'hsl(43, 50%, 25%)' }}>
                  <div className="flex items-center justify-between mb-1">
                    <span className="font-semibold text-sm">{otp.user_name}</span>
                    <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${roleColors[otp.role] || ''}`}>{(otp.role || '').replace(/_/g, ' ')}</span>
                  </div>
                  <p className="text-xs text-muted-foreground">@{otp.username}</p>
                  <div className="flex items-center justify-between mt-2">
                    <span className="text-2xl font-mono font-bold tracking-[0.3em]" style={{ color: 'hsl(43, 96%, 56%)' }} data-testid="otp-code">{otp.otp}</span>
                    <span className="text-xs text-muted-foreground flex items-center gap-1"><Clock size={10} />{formatDateTime(otp.created_at)}</span>
                  </div>
                </div>
              ))}
            </div>
          ) : <p className="text-sm text-muted-foreground text-center py-6">No pending OTPs</p>}
        </DialogContent>
      </Dialog>

      {/* Sessions Dialog */}
      <Dialog open={showSessions} onOpenChange={setShowSessions}>
        <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Monitor size={18} /> Active Sessions ({sessions.length})</DialogTitle>
          </DialogHeader>
          {isAdmin && sessions.length > 0 && (
            <Button variant="destructive" size="sm" className="w-full mb-2" onClick={terminateAllSessions} data-testid="end-all-sessions-btn">
              <XCircle size={14} className="mr-1" /> End All Sessions
            </Button>
          )}
          {sessions.length > 0 ? (
            <div className="space-y-2">
              {sessions.map(s => (
                <div key={s.id} className="p-3 rounded-lg border bg-muted/30" data-testid={`session-item-${s.id}`}>
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <p className="font-semibold text-sm">{s.user_name}</p>
                        <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${roleColors[s.role] || ''}`}>{(s.role || '').replace(/_/g, ' ')}</span>
                        {s.user_login_count > 1 && (
                          <span className="px-1.5 py-0.5 rounded-full text-[10px] font-bold bg-amber-500/20 text-amber-400 border border-amber-600/30">
                            {s.user_login_count} logins
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                        <Monitor size={11} className="shrink-0" />
                        <span className="truncate">{s.device_name || 'Unknown'}</span>
                      </div>
                      <div className="flex items-center gap-1.5 text-xs text-muted-foreground mt-0.5">
                        <span className="font-mono">{s.ip_address || 'Unknown'}</span>
                      </div>
                      <p className="text-[10px] text-muted-foreground mt-1 flex items-center gap-1">
                        <Clock size={10} /> {formatDateTime(s.login_at)}
                      </p>
                    </div>
                    <Button variant="ghost" size="sm" className="text-destructive text-xs shrink-0" onClick={() => terminateSession(s.id)} data-testid={`session-terminate-${s.id}`}>
                      <XCircle size={14} />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          ) : <p className="text-sm text-muted-foreground text-center py-6">No active sessions</p>}
        </DialogContent>
      </Dialog>
    </div>
  );
}
